import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, InputAdornment, Divider, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ReactMarkdown from 'react-markdown';
import ResponsiveAdminPage from '../../../components/admin/ResponsiveAdminPage';
import { USER_MANUAL_MARKDOWN } from './manualContent';

const PRIMARY = '#f25d27';

// Remove o título principal (a página já exibe seu próprio cabeçalho) e o
// bloco de sumário em markdown (substituído pela navegação lateral interativa).
const TITLE_LINE_REGEX = /^# .+\n\n/;
const SUMMARY_BLOCK_REGEX = /## Sumário[\s\S]*?\n---\n/;

function slugify(text) {
    return text
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function getText(children) {
    return React.Children.toArray(children)
        .map((child) => {
            if (typeof child === 'string') return child;
            if (child?.props?.children) return getText(child.props.children);
            return '';
        })
        .join('');
}

export default function AdminUserManualPage() {
    const [tocFilter, setTocFilter] = useState('');

    const tocItems = useMemo(() => (
        [...USER_MANUAL_MARKDOWN.matchAll(/^##\s+(.+)$/gm)]
            .map(([, title]) => title.trim())
            .filter((title) => title !== 'Sumário')
            .map((title) => ({ title, id: slugify(title) }))
    ), []);

    const content = useMemo(() => (
        USER_MANUAL_MARKDOWN
            .replace(TITLE_LINE_REGEX, '')
            .replace(SUMMARY_BLOCK_REGEX, '')
    ), []);

    const filteredToc = tocFilter
        ? tocItems.filter((item) => item.title.toLowerCase().includes(tocFilter.toLowerCase()))
        : tocItems;

    const handleTocClick = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const markdownComponents = useMemo(() => ({
        h1: ({ children }) => (
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#1a1a1a', mt: 0, mb: 2 }}>
                {children}
            </Typography>
        ),
        h2: ({ children }) => {
            const id = slugify(getText(children));
            return (
                <Typography
                    id={id}
                    variant="h6"
                    component="h2"
                    sx={{
                        fontWeight: 700,
                        color: '#1a1a1a',
                        mt: 5,
                        mb: 2,
                        pb: 1,
                        borderBottom: '2px solid #f25d2726',
                        scrollMarginTop: '24px',
                    }}
                >
                    {children}
                </Typography>
            );
        },
        h3: ({ children }) => (
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700, color: PRIMARY, mt: 3, mb: 1.2 }}>
                {children}
            </Typography>
        ),
        p: ({ children }) => (
            <Typography component="p" sx={{ mb: 1.6, color: '#333', lineHeight: 1.75, fontSize: '0.94rem' }}>
                {children}
            </Typography>
        ),
        ul: ({ children }) => (
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                {children}
            </Box>
        ),
        ol: ({ children }) => (
            <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                {children}
            </Box>
        ),
        li: ({ children }) => (
            <Typography component="li" sx={{ mb: 0.6, color: '#333', lineHeight: 1.7, fontSize: '0.94rem' }}>
                {children}
            </Typography>
        ),
        a: ({ href, children }) => (
            <Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: PRIMARY, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
                {children}
            </Box>
        ),
        strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                {children}
            </Box>
        ),
        code: ({ children }) => (
            <Box
                component="code"
                sx={{
                    backgroundColor: `${PRIMARY}14`,
                    color: PRIMARY,
                    px: 0.7,
                    py: 0.15,
                    borderRadius: '4px',
                    fontSize: '0.85em',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                }}
            >
                {children}
            </Box>
        ),
        hr: () => <Divider sx={{ my: 4 }} />,
        blockquote: ({ children }) => (
            <Box
                sx={{
                    borderLeft: `3px solid ${PRIMARY}`,
                    backgroundColor: `${PRIMARY}08`,
                    pl: 2,
                    pr: 2,
                    py: 1,
                    my: 2,
                    borderRadius: '0 8px 8px 0',
                    color: '#555',
                    fontStyle: 'italic',
                    '& p': { mb: 0 },
                }}
            >
                {children}
            </Box>
        ),
    }), []);

    return (
        <ResponsiveAdminPage>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.4 }}>
                <MenuBookOutlinedIcon sx={{ color: PRIMARY }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
                    Manual do Usuário
                </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: '#999', fontSize: '0.78rem', display: 'block', mb: 3 }}>
                Guia completo de telas e funcionalidades do NeoFeed Backoffice
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: { xs: '100%', md: 280 },
                        flexShrink: 0,
                        position: { md: 'sticky' },
                        top: { md: 8 },
                        border: '1px solid #ececec',
                        borderRadius: '8px',
                        p: 1.5,
                        maxHeight: { md: 'calc(100dvh - 120px)' },
                        overflowY: { md: 'auto' },
                    }}
                >
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Pesquisar seção..."
                        value={tocFilter}
                        onChange={(e) => setTocFilter(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 1.5,
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
                        }}
                    />
                    <Box component="nav" aria-label="Sumário do manual" sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                        {filteredToc.map((item) => (
                            <Box
                                key={item.id}
                                component="button"
                                type="button"
                                onClick={() => handleTocClick(item.id)}
                                sx={{
                                    textAlign: 'left',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    py: 0.65,
                                    px: 1,
                                    borderRadius: '6px',
                                    fontSize: '0.82rem',
                                    color: '#555',
                                    fontFamily: 'inherit',
                                    '&:hover': { backgroundColor: `${PRIMARY}0c`, color: PRIMARY },
                                }}
                            >
                                {item.title}
                            </Box>
                        ))}
                        {filteredToc.length === 0 && (
                            <Typography sx={{ fontSize: '0.8rem', color: '#999', px: 1 }}>
                                Nenhuma seção encontrada.
                            </Typography>
                        )}
                    </Box>
                </Paper>

                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: '100%',
                        border: '1px solid #ececec',
                        borderRadius: '8px',
                        p: { xs: 2.5, md: 4 },
                    }}
                >
                    <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
                </Paper>
            </Box>
        </ResponsiveAdminPage>
    );
}
